import axios from 'axios';
import config from './config';

/**
 * App-wide session-expiry watcher.
 *
 * Most pages call the browser's raw `fetch()` directly (29 files); a handful use `axios` (10
 * files). Neither path previously reacted to a 401 — AuthContext only validated the token once,
 * on page load, so a token expiring mid-session just made every subsequent request fail silently
 * with whatever generic error message that one component happened to show.
 *
 * Wrapping `window.fetch` once here (instead of editing all 29 call sites) plus one axios
 * interceptor gives every request the same reaction: dispatch `auth:session-expired`, which
 * AuthContext listens for to log out and redirect to /login with an explanatory message.
 *
 * Auth-flow endpoints are excluded — a 401 from /api/login is "wrong password", not "your
 * session expired", and must not trigger a logout/redirect. Same reasoning covers any
 * already-logged-in step-up-auth check that reuses 401 for "wrong current password" (e.g.
 * changing your password, resetting MFA after losing your device) — those calls carry a
 * perfectly valid session token; the 401 is about a *different* password field, not the JWT.
 */

const EXCLUDED_PATHS = [
  '/api/login',
  '/api/register',
  '/api/forgot-password',
  '/api/reset-password',
  '/api/verify-token',
  '/api/change-password',
  '/api/mfa/enroll',
  // Deliberately 401s ("needs_reauth: true") whenever Google/Gmail simply isn't connected yet,
  // or its own refresh token has gone stale — called automatically every time IntegrationsPage
  // loads. That 401 is about the Google OAuth link, not this app's own JWT session, so it must
  // not trigger a logout (same reasoning as the entries above).
  '/api/auth/google/refresh',
];

const isExcluded = (url: string) => EXCLUDED_PATHS.some(p => url.includes(p));

const notifySessionExpired = () => {
  // Only meaningful if we actually thought we were logged in — a 401 on a public page with no
  // stored token is normal (e.g. an anonymous status check), not an expired session.
  if (localStorage.getItem('auth_token')) {
    window.dispatchEvent(new Event('auth:session-expired'));
  }
};

let initialized = false;

export function initSessionExpiryWatcher() {
  if (initialized) return; // idempotency guard — safe if ever called more than once
  initialized = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const response = await originalFetch(...args);
    const input = args[0];
    const url = typeof input === 'string' ? input : (input as Request).url;
    if (response.status === 401 && url.includes(config.apiUrl) && !isExcluded(url)) {
      notifySessionExpired();
    }
    return response;
  };

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const url: string = error?.config?.url || '';
      if (error?.response?.status === 401 && !isExcluded(url)) {
        notifySessionExpired();
      }
      return Promise.reject(error);
    }
  );
}
